import { defineStore } from "pinia";
import { computed, isProxy, isReactive, isRef, ref, toRaw, watch } from "vue";
import * as Y from "yjs";
import { useYjsProviderStore } from "./yjs-provider";

function canonicalId(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value.id || null;
}

function sanitizeForYjs(value) {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;

  if (isRef(value)) {
    return sanitizeForYjs(value.value);
  }

  const raw = toRaw(value);
  if (isProxy(raw) || isReactive(raw)) {
    console.warn("[inventory] sanitizeForYjs received reactive value");
  }

  if (raw instanceof Date) {
    return raw.toISOString();
  }

  if (Array.isArray(raw)) {
    return raw.map((entry) => sanitizeForYjs(entry));
  }

  const output = {};
  Object.entries(raw).forEach(([key, entry]) => {
    output[key] = sanitizeForYjs(entry);
  });
  return output;
}

function toPlain(value) {
  if (value instanceof Y.Map) {
    const obj = {};
    value.forEach((entry, key) => {
      obj[key] = toPlain(entry);
    });
    return obj;
  }

  if (value instanceof Y.Array) {
    return value.toArray().map((entry) => toPlain(entry));
  }

  if (value instanceof Y.Text) {
    return value.toString();
  }

  return value;
}

function toYValue(value) {
  if (value instanceof Y.Map || value instanceof Y.Array || value instanceof Y.Text) {
    return value;
  }

  const clean = sanitizeForYjs(value);

  if (Array.isArray(clean)) {
    const yArray = new Y.Array();
    yArray.push(clean.map((entry) => toYValue(entry)));
    return yArray;
  }

  if (clean && typeof clean === "object") {
    const yMap = new Y.Map();
    Object.entries(clean).forEach(([key, entry]) => {
      yMap.set(key, toYValue(entry));
    });
    return yMap;
  }

  return clean;
}

function wellKey(puckId, wellNo) {
  return `${puckId}:${wellNo}`;
}

function rackKey(dewarId, position) {
  return `${dewarId}:${position}`;
}

function puckKey(rackId, position) {
  return `${rackId}:${position}`;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function isCascadeDeletedLamella(lamella, gridId) {
  return lamella.get("gridId") === gridId && Boolean(lamella.get("deletedByParentCascade"));
}

function gridSlotName(position) {
  switch (position) {
    case 1:
      return "gridTopLeft";
    case 2:
      return "gridTopRight";
    case 3:
      return "gridBottomLeft";
    case 4:
      return "gridBottomRight";
    default:
      return null;
  }
}

function gridSlotPosition(name) {
  switch (name) {
    case "gridTopLeft":
      return 1;
    case "gridTopRight":
      return 2;
    case "gridBottomLeft":
      return 3;
    case "gridBottomRight":
      return 4;
    default:
      return null;
  }
}

function isBlankGridPayload(value) {
  if (!value || typeof value !== "object") return true;
  const gridType = Array.isArray(value.gridType) ? value.gridType : [];
  return !value.name && !value.description && !value.imageurl && !value.trueDateCreated && !value.gridCatalogType && !value.forTomography && !gridType.some(Boolean);
}

function getApiHost() {
  let host = import.meta.env.VITE_APP_HOST || "";
  if (typeof window !== "undefined" && window.location.hostname === "devtest.subangstrom.com") {
    host = "https://nodedev.subangstrom.com";
  }
  return host;
}

export const useInventoryStore = defineStore("inventory", () => {
  const providerStore = useYjsProviderStore();

  const dewars = ref(new Map());
  const racks = ref(new Map());
  const pucks = ref(new Map());
  const boxes = ref(new Map());
  const grids = ref(new Map());
  const lamellae = ref(new Map());
  const wellOccupancy = ref(new Map());
  const rackOccupancy = ref(new Map());
  const puckOccupancy = ref(new Map());
  const mutationStatuses = ref(new Map());
  const isInitialized = ref(false);
  const lastSyncTime = ref(null);

  let ydoc = null;
  let yDewars = null;
  let yRacks = null;
  let yPucks = null;
  let yBoxes = null;
  let yGrids = null;
  let yLamellae = null;
  let yWellOccupancy = null;
  let yRackOccupancy = null;
  let yPuckOccupancy = null;
  const observerCleanup = [];

  function ensureYjs(map, name) {
    if (!ydoc || !map) {
      throw new Error(`Yjs not initialized for ${name}`);
    }
  }

  function getMutationUserId() {
    return providerStore.sessionData?.userId || null;
  }

  function shouldUseScopedRest() {
    return (
      !ydoc ||
      providerStore.sessionData?.readOnly === true ||
      providerStore.sessionData?.access === "readonly"
    );
  }

  function mutationStatusKey(entityType, id = "create") {
    return `${entityType}:${id || "create"}`;
  }

  function setMutationStatus(entityType, id, status, error = null) {
    const next = new Map(mutationStatuses.value);
    next.set(mutationStatusKey(entityType, id), {
      type: entityType,
      id: id || null,
      status,
      error: error ? String(error?.message || error) : null,
      updatedAt: new Date().toISOString(),
    });
    mutationStatuses.value = next;
  }

  function getMutationStatus(entityType, id = "create") {
    return mutationStatuses.value.get(mutationStatusKey(entityType, id)) || {
      type: entityType,
      id: id || null,
      status: "idle",
      error: null,
      updatedAt: null,
    };
  }

  async function scopedMutation(entityType, method, id = null, data = undefined) {
    const pathId = id ? `/${encodeURIComponent(id)}` : "";
    setMutationStatus(entityType, id, "saving");
    try {
      const response = await fetch(`${getApiHost()}/api/v1/yjs/inventory/${entityType}${pathId}`, {
        credentials: "include",
        method,
        headers: { "content-type": "application/json" },
        body: data === undefined ? undefined : JSON.stringify({ data: sanitizeForYjs(data) }),
      });
      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};
      if (!response.ok || payload.status === "fail" || payload.status === "error") {
        throw new Error(payload.message || "Scoped inventory mutation failed");
      }
      setMutationStatus(entityType, id, "idle");
      return payload.data ?? null;
    } catch (error) {
      setMutationStatus(entityType, id, "error", error);
      throw error;
    }
  }

  async function scopedCreate(entityType, data) {
    const created = await scopedMutation(entityType, "POST", null, data);
    return canonicalId(created);
  }

  function scopedUpdate(entityType, id, data) {
    return scopedMutation(entityType, "PATCH", id, data);
  }

  function scopedDelete(entityType, id) {
    return scopedMutation(entityType, "DELETE", id);
  }

  async function scopedRestore(entityType, id) {
    setMutationStatus(entityType, id, "saving");
    try {
      const response = await fetch(
        `${getApiHost()}/api/v1/yjs/inventory/${entityType}/${encodeURIComponent(id)}/restore`,
        {
          credentials: "include",
          method: "POST",
          headers: { "content-type": "application/json" },
        }
      );
      const text = await response.text();
      const payload = text ? JSON.parse(text) : {};
      if (!response.ok || payload.status === "fail" || payload.status === "error") {
        throw new Error(payload.message || "Scoped inventory restore failed");
      }
      setMutationStatus(entityType, id, "idle");
      return payload.data ?? null;
    } catch (error) {
      setMutationStatus(entityType, id, "error", error);
      throw error;
    }
  }

  function syncMapState(source, target) {
    target.value.clear();
    if (!source) return;
    source.forEach((value, key) => {
      target.value.set(key, toPlain(value));
    });
  }

  function syncStringMapState(source, target) {
    target.value.clear();
    if (!source) return;
    source.forEach((value, key) => {
      target.value.set(key, value);
    });
  }

  function syncFromYjs() {
    syncMapState(yDewars, dewars);
    syncMapState(yRacks, racks);
    syncMapState(yPucks, pucks);
    syncMapState(yBoxes, boxes);
    syncMapState(yGrids, grids);
    syncMapState(yLamellae, lamellae);
    syncStringMapState(yWellOccupancy, wellOccupancy);
    syncStringMapState(yRackOccupancy, rackOccupancy);
    syncStringMapState(yPuckOccupancy, puckOccupancy);
    lastSyncTime.value = new Date().toISOString();
  }

  function cleanupObservers() {
    observerCleanup.splice(0).forEach((cleanup) => cleanup());
  }

  function setupObservers() {
    cleanupObservers();
    [
      [yDewars, () => syncMapState(yDewars, dewars)],
      [yRacks, () => syncMapState(yRacks, racks)],
      [yPucks, () => syncMapState(yPucks, pucks)],
      [yBoxes, () => syncMapState(yBoxes, boxes)],
      [yGrids, () => syncMapState(yGrids, grids)],
      [yLamellae, () => syncMapState(yLamellae, lamellae)],
      [yWellOccupancy, () => syncStringMapState(yWellOccupancy, wellOccupancy)],
      [yRackOccupancy, () => syncStringMapState(yRackOccupancy, rackOccupancy)],
      [yPuckOccupancy, () => syncStringMapState(yPuckOccupancy, puckOccupancy)],
    ].forEach(([map, observer]) => {
      if (!map) return;
      map.observeDeep(observer);
      observerCleanup.push(() => map.unobserveDeep(observer));
    });
  }

  function initializeFromYjs() {
    if (!providerStore.ydoc) {
      return;
    }

    ydoc = providerStore.ydoc;
    yDewars = ydoc.getMap("dewars");
    yRacks = ydoc.getMap("racks");
    yPucks = ydoc.getMap("pucks");
    yBoxes = ydoc.getMap("boxes");
    yGrids = ydoc.getMap("grids");
    yLamellae = ydoc.getMap("lamellae");
    yWellOccupancy = ydoc.getMap("wellOccupancy");
    yRackOccupancy = ydoc.getMap("rackOccupancy");
    yPuckOccupancy = ydoc.getMap("puckOccupancy");

    syncFromYjs();
    setupObservers();
    isInitialized.value = true;
  }

  function cleanup() {
    cleanupObservers();
    ydoc = null;
    yDewars = null;
    yRacks = null;
    yPucks = null;
    yBoxes = null;
    yGrids = null;
    yLamellae = null;
    yWellOccupancy = null;
    yRackOccupancy = null;
    yPuckOccupancy = null;
    [dewars, racks, pucks, boxes, grids, lamellae, wellOccupancy, rackOccupancy, puckOccupancy].forEach(
      (entry) => entry.value.clear()
    );
    isInitialized.value = false;
    lastSyncTime.value = null;
  }

  function appendPendingLog(entityType, entityId, verb, changes) {
    const userId = getMutationUserId();
    if (!userId || !ydoc) return;

    const pendingLogsMap = ydoc.getMap("pendingLogsMap");
    const logId = crypto.randomUUID();
    pendingLogsMap.set(
      logId,
      toYValue({
        id: logId,
        userId,
        entityType,
        entityId,
        verb,
        changes,
        timestamp: new Date().toISOString(),
      })
    );
  }

  function computeFieldChanges(oldEntity, newEntity) {
    const skip = new Set(["id", "labId", "dateCreated", "dateModified", "deletedAt"]);
    const changes = [];

    Object.keys(newEntity).forEach((key) => {
      if (skip.has(key) || key.startsWith("_")) return;
      const from = oldEntity ? oldEntity[key] ?? null : null;
      const to = newEntity[key];
      if (JSON.stringify(from) !== JSON.stringify(to)) {
        changes.push({ field: key, from, to });
      }
    });

    return changes;
  }

  function setEntityFields(yMap, payload, { replaceSlots = false } = {}) {
    Object.entries(payload).forEach(([key, value]) => {
      if (replaceSlots && key === "slots" && Array.isArray(value)) {
        yMap.set(key, toYValue(value));
        return;
      }
      yMap.set(key, toYValue(value));
    });
  }

  function getDewar(id) {
    return dewars.value.get(id) || null;
  }

  function getRack(id) {
    return racks.value.get(id) || null;
  }

  function getPuck(id) {
    return pucks.value.get(id) || null;
  }

  function getBox(id) {
    return boxes.value.get(id) || null;
  }

  function getGrid(id) {
    return grids.value.get(id) || null;
  }

  function getLamella(id) {
    return lamellae.value.get(id) || null;
  }

  const dewarsList = computed(() => Array.from(dewars.value.values()).filter((entry) => !entry.deletedAt));
  const racksList = computed(() => Array.from(racks.value.values()).filter((entry) => !entry.deletedAt));
  const pucksList = computed(() => Array.from(pucks.value.values()).filter((entry) => !entry.deletedAt));
  const boxesList = computed(() => Array.from(boxes.value.values()).filter((entry) => !entry.deletedAt));
  const gridsList = computed(() => Array.from(grids.value.values()).filter((entry) => !entry.deletedAt));
  const lamellaeList = computed(() => Array.from(lamellae.value.values()).filter((entry) => !entry.deletedAt));
  const deletedEntities = computed(() => {
    const deleted = [];
    const pushDeleted = (type, entity, deletedAt) => {
      deleted.push({
        ...clone(entity),
        type,
        id: entity.id || entity.id,
        deletedAt: deletedAt || entity.dateModified || entity.dateCreated || null,
      });
    };

    dewars.value.forEach((entity) => {
      if (entity.deletedAt) pushDeleted("dewar", entity, entity.deletedAt);
    });
    racks.value.forEach((entity) => {
      if (entity.deletedAt) pushDeleted("rack", entity, entity.deletedAt);
    });
    pucks.value.forEach((entity) => {
      if (entity.deletedAt) pushDeleted("puck", entity, entity.deletedAt);
    });
    boxes.value.forEach((entity) => {
      if (entity.deletedAt) pushDeleted("box", entity, entity.deletedAt);
    });
    grids.value.forEach((entity) => {
      if (entity.deletedAt || entity.archived) {
        pushDeleted("grid", entity, entity.deletedAt || entity.dateModified);
      }
    });
    lamellae.value.forEach((entity) => {
      if (entity.deletedAt) pushDeleted("lamella", entity, entity.deletedAt);
    });

    return deleted.sort((a, b) => new Date(b.deletedAt || 0) - new Date(a.deletedAt || 0));
  });
  const getUnassignedRacks = computed(() =>
    racksList.value.filter((entry) => !entry.dewarId)
  );
  const getUnassignedPucks = computed(() =>
    pucksList.value.filter((entry) => !entry.rackId)
  );

  function getRacksForDewar(dewarId) {
    return racksList.value.filter((entry) => entry.dewarId === dewarId);
  }

  function getPucksForRack(rackId) {
    return pucksList.value.filter((entry) => entry.rackId === rackId);
  }

  function getBoxesForPuck(puckId) {
    return boxesList.value.filter((entry) => entry.puckId === puckId);
  }

  function getGridsForBox(boxId) {
    return gridsList.value.filter((entry) => entry.boxId === boxId && !entry.archived);
  }

  function getLamellaeForGrid(gridId) {
    return lamellaeList.value.filter((entry) => entry.gridId === gridId);
  }

  function getArchivedGrids() {
    return gridsList.value
      .filter((entry) => entry.archived)
      .sort((a, b) => new Date(b.dateModified || b.dateCreated) - new Date(a.dateModified || a.dateCreated));
  }

  function getLamellaSummaryForGrid(gridId) {
    const summary = {
      total: 0,
      active: 0,
      completed: 0,
      alert: 0,
      pending: 0,
      imageCount: 0,
      latestStage: null,
      latestStatus: null,
      latestTimestamp: null,
    };

    let latestTs = 0;
    for (const lamella of getLamellaeForGrid(gridId)) {
      summary.total += 1;
      const status = (lamella.status || 'pending').toLowerCase();
      if (status.includes('fail') || status.includes('error')) {
        summary.alert += 1;
      } else if (status.includes('done') || status.includes('complete')) {
        summary.completed += 1;
      } else if (status.includes('milling') || status.includes('lift') || status.includes('progress')) {
        summary.active += 1;
      } else {
        summary.pending += 1;
      }

      summary.imageCount += lamella.imageRefs?.length || 0;

      const timestamp =
        lamella.lastObservedAt ||
        lamella.completedAt ||
        lamella.startedAt ||
        lamella.dateModified ||
        lamella.dateCreated;
      const parsed = timestamp ? new Date(timestamp).getTime() : 0;
      if (parsed >= latestTs) {
        latestTs = parsed;
        summary.latestStage = lamella.currentStage || null;
        summary.latestStatus = lamella.status || null;
        summary.latestTimestamp = timestamp || null;
      }
    }

    return summary;
  }

  function getBoxAtWell(puckId, wellNo) {
    const boxId = wellOccupancy.value.get(wellKey(puckId, wellNo));
    return boxId ? getBox(boxId) : null;
  }

  function findGridEntryByBoxAndPosition(boxId, position) {
    let found = null;
    yGrids?.forEach((grid, gridId) => {
      if (found) return;
      const plainGrid = toPlain(grid);
      if (plainGrid.deletedAt || plainGrid.archived) return;
      if (plainGrid.boxId === boxId && Number(plainGrid.position) === Number(position)) {
        found = { id: gridId, map: grid, plain: plainGrid };
      }
    });
    return found;
  }

  function parseGridPosition(value) {
    const position = Number(value);
    if (!Number.isInteger(position) || position < 1) {
      throw new Error("Grid position must be a positive integer");
    }
    return position;
  }

  function assertGridSlotAvailable(boxId, position, currentGridId = null) {
    yGrids?.forEach((grid, gridId) => {
      if (gridId === currentGridId) return;
      const plainGrid = toPlain(grid);
      if (plainGrid.deletedAt || plainGrid.archived) return;
      if (plainGrid.boxId !== boxId) return;
      if (Number(plainGrid.position) !== position) return;
      const error = new Error("Target grid slot is already occupied");
      error.statusCode = 409;
      throw error;
    });
  }

  function resolveGridPlacement(gridId, currentGrid = null, updates = {}) {
    const archived = updates.archived ?? currentGrid?.archived ?? false;
    if (archived) {
      return { archived: true, boxId: null, position: null };
    }

    const boxId =
      updates.boxId !== undefined
        ? canonicalId(updates.boxId)
        : canonicalId(currentGrid?.boxId) || canonicalId(currentGrid?.previousBoxId);
    if (!boxId) {
      const error = new Error("Active grids must include a boxId");
      error.statusCode = 400;
      throw error;
    }

    const position = parseGridPosition(
      updates.position !== undefined
        ? updates.position
        : currentGrid?.position ?? currentGrid?.previousPosition ?? 1
    );
    assertGridSlotAvailable(boxId, position, gridId);

    return { archived: false, boxId, position };
  }

  function cascadeDeleteLamellaeForGrid(gridId, now) {
    if (!yLamellae) return;

    yLamellae.forEach((lamella, lamellaId) => {
      if (lamella.get("gridId") !== gridId) return;
      if (lamella.get("deletedAt")) return;

      setEntityFields(lamella, {
        deletedAt: now,
        deletedByParentCascade: true,
        dateModified: now,
      });
      appendPendingLog("lamella", lamellaId, "deleted");
    });
  }

  function restoreCascadeDeletedLamellaeForGrid(gridId, now) {
    if (!yLamellae) return;

    yLamellae.forEach((lamella, lamellaId) => {
      if (!isCascadeDeletedLamella(lamella, gridId)) return;

      lamella.delete("deletedAt");
      setEntityFields(lamella, {
        deletedByParentCascade: false,
        dateModified: now,
      });
      appendPendingLog("lamella", lamellaId, "restored");
    });
  }

  function applyLegacyGridSlotUpdates(boxId, updates) {
    const slotUpdates = Object.entries(updates).filter(([key]) => gridSlotPosition(key));
    if (!slotUpdates.length) return;

    slotUpdates.forEach(([slotName, slotValue]) => {
      const position = gridSlotPosition(slotName);
      const existing = findGridEntryByBoxAndPosition(boxId, position);
      const now = new Date().toISOString();
      const nextId = existing?.id || crypto.randomUUID();

      if (isBlankGridPayload(slotValue)) {
        if (existing) {
          existing.map.delete("deletedAt");
          setEntityFields(existing.map, {
            archived: true,
            boxId: null,
            position: null,
            dateModified: now,
          });
          appendPendingLog("grid", existing.id, "deleted");
        }
        return;
      }

      const nextPayload = {
        ...existing?.plain,
        id: nextId,
        boxId,
        position,
        archived: false,
        name: slotValue?.name ?? existing?.plain?.name ?? "",
        description: slotValue?.description ?? existing?.plain?.description ?? "",
        gridType: slotValue?.gridType ?? existing?.plain?.gridType ?? [],
        gridCatalogType: slotValue?.gridCatalogType ?? existing?.plain?.gridCatalogType ?? null,
        forTomography: slotValue?.forTomography ?? existing?.plain?.forTomography ?? false,
        imageurl: slotValue?.imageurl ?? existing?.plain?.imageurl ?? null,
        imageRefs: existing?.plain?.imageRefs ?? [],
        trueDateCreated:
          slotValue?.trueDateCreated ??
          existing?.plain?.trueDateCreated ??
          now,
        dateCreated: existing?.plain?.dateCreated || now,
        dateModified: now,
      };

      if (existing) {
        setEntityFields(existing.map, nextPayload);
        const changes = computeFieldChanges(existing.plain, toPlain(existing.map));
        if (changes.length) appendPendingLog("grid", existing.id, "updated", changes);
      } else {
        const grid = new Y.Map();
        yGrids.set(nextPayload.id, grid);
        setEntityFields(grid, nextPayload);
        appendPendingLog("grid", nextPayload.id, "created");
      }
    });
  }

  function moveGrid(sourceBoxId, sourceSlotName, targetPuckId, targetWellNo, targetSlotName) {
    ensureYjs(yGrids, "grids");
    const sourcePosition = gridSlotPosition(sourceSlotName);
    const targetPosition = gridSlotPosition(targetSlotName);
    if (!sourcePosition || !targetPosition) {
      throw new Error("Invalid grid slot");
    }

    const targetBox = getBoxAtWell(targetPuckId, targetWellNo);
    if (!targetBox?.id) {
      throw new Error("Target box not found for grid move");
    }

    const sourceGrid = findGridEntryByBoxAndPosition(sourceBoxId, sourcePosition);
    if (!sourceGrid) {
      throw new Error("Source grid not found");
    }

    const existingTarget = findGridEntryByBoxAndPosition(targetBox.id, targetPosition);
    if (existingTarget && existingTarget.id !== sourceGrid.id) {
      throw new Error("Target grid slot is already occupied");
    }

    if (shouldUseScopedRest()) {
      return scopedUpdate("grids", sourceGrid.id, {
        boxId: targetBox.id,
        position: targetPosition,
        archived: false,
      });
    }

    ydoc.transact(() => {
      setEntityFields(sourceGrid.map, {
        archived: false,
        boxId: targetBox.id,
        position: targetPosition,
        dateModified: new Date().toISOString(),
      });
      sourceGrid.map.delete("deletedAt");
      const changes = computeFieldChanges(sourceGrid.plain, toPlain(sourceGrid.map));
      if (changes.length) {
        appendPendingLog("grid", sourceGrid.id, "moved", changes);
      }
    }, "inventory:move-grid");
  }

  function createDewar(data = {}) {
    if (shouldUseScopedRest()) {
      return scopedCreate("dewars", data);
    }
    ensureYjs(yDewars, "dewars");
    const id = data.id || crypto.randomUUID();
    const now = new Date().toISOString();

    ydoc.transact(() => {
      const dewar = new Y.Map();
      yDewars.set(id, dewar);
      setEntityFields(dewar, {
        id,
        id,
        title: data.title || data.name || "New Dewar",
        name: data.title || data.name || "New Dewar",
        customId: data.customId || "",
        description: data.description || data.details || "",
        details: data.details || data.description || "",
        serialNo: data.serialNo || "",
        capacity: Number(data.capacity || 10),
        labId: data.labId || providerStore.currentLabId || "",
        creatorId: getMutationUserId(),
        dateCreated: data.dateCreated || now,
        dateModified: now,
      });
      appendPendingLog("dewar", id, "created");
    }, "inventory:create-dewar");

    return id;
  }

  function updateDewar(id, updates = {}) {
    if (shouldUseScopedRest()) {
      return scopedUpdate("dewars", id, updates);
    }
    ensureYjs(yDewars, "dewars");
    const dewar = yDewars.get(id);
    if (!dewar) throw new Error(`Dewar not found: ${id}`);
    const oldEntity = toPlain(dewar);
    const next = {
      ...updates,
      dateModified: new Date().toISOString(),
    };

    ydoc.transact(() => {
      setEntityFields(dewar, next);
      const changes = computeFieldChanges(oldEntity, toPlain(dewar));
      if (changes.length) appendPendingLog("dewar", id, "updated", changes);
    }, "inventory:update-dewar");
  }

  function deleteDewar(id) {
    if (shouldUseScopedRest()) {
      return scopedDelete("dewars", id);
    }
    ensureYjs(yDewars, "dewars");
    ensureYjs(yRacks, "racks");
    const dewar = yDewars.get(id);
    if (!dewar) throw new Error(`Dewar not found: ${id}`);
    const now = new Date().toISOString();

    ydoc.transact(() => {
      yRacks.forEach((rack, rackId) => {
        const plainRack = toPlain(rack);
        if (plainRack.deletedAt || plainRack.dewarId !== id) return;
        if (plainRack.dewarPosition !== null && plainRack.dewarPosition !== undefined) {
          yRackOccupancy.delete(rackKey(id, plainRack.dewarPosition));
        }
        setEntityFields(rack, {
          dewarId: null,
          dewarPosition: null,
          position: null,
          dateModified: now,
        });
        appendPendingLog("rack", rackId, "updated", [
          { field: "dewarId", from: plainRack.dewarId, to: null },
        ]);
      });
      setEntityFields(dewar, { deletedAt: now, dateModified: now });
      appendPendingLog("dewar", id, "deleted");
    }, "inventory:delete-dewar");
  }

  function createRack(data = {}) {
    if (shouldUseScopedRest()) {
      return scopedCreate("racks", data);
    }
    ensureYjs(yRacks, "racks");
    ensureYjs(yRackOccupancy, "rackOccupancy");
    const id = data.id || crypto.randomUUID();
    const now = new Date().toISOString();
    const position = data.dewarPosition ?? data.position ?? null;

    ydoc.transact(() => {
      if (data.dewarId && position !== null && position !== undefined) {
        const key = rackKey(data.dewarId, position);
        const occupied = yRackOccupancy.get(key);
        if (occupied && occupied !== id) {
          throw new Error("Rack position is already occupied");
        }
      }

      const rack = new Y.Map();
      yRacks.set(id, rack);
      setEntityFields(rack, {
        id,
        id,
        title: data.title || data.name || "",
        name: data.title || data.name || "",
        description: data.description || "",
        customId: data.customId || "",
        rackType: data.rackType || "Storage",
        serialNo: data.serialNo || data.customId || id,
        dewarId: data.dewarId || null,
        dewarPosition: position,
        position,
        projectId: data.projectId || data.project || null,
        project: data.project || data.projectId || null,
        inTransport: !!data.inTransport,
        labId: data.labId || providerStore.currentLabId || "",
        creatorId: getMutationUserId(),
        dateCreated: data.dateCreated || now,
        dateModified: now,
      });

      if (data.dewarId && position !== null && position !== undefined) {
        yRackOccupancy.set(rackKey(data.dewarId, position), id);
      }
      appendPendingLog("rack", id, "created");
    }, "inventory:create-rack");

    return id;
  }

  function updateRack(id, updates = {}) {
    if (shouldUseScopedRest()) {
      return scopedUpdate("racks", id, updates);
    }
    ensureYjs(yRacks, "racks");
    const rack = yRacks.get(id);
    if (!rack) throw new Error(`Rack not found: ${id}`);
    const oldEntity = toPlain(rack);

    ydoc.transact(() => {
      const oldDewarId = oldEntity.dewarId || null;
      const oldPosition = oldEntity.dewarPosition ?? oldEntity.position ?? null;
      const nextDewarId =
        updates.dewarId !== undefined ? updates.dewarId : updates.removeRack ? null : oldDewarId;
      const nextPosition =
        updates.dewarPosition !== undefined || updates.position !== undefined
          ? updates.dewarPosition ?? updates.position ?? null
          : updates.removeRack
            ? null
            : oldPosition;

      if (oldDewarId && oldPosition !== null && oldPosition !== undefined) {
        yRackOccupancy.delete(rackKey(oldDewarId, oldPosition));
      }
      if (nextDewarId && nextPosition !== null && nextPosition !== undefined) {
        const nextKey = rackKey(nextDewarId, nextPosition);
        const occupied = yRackOccupancy.get(nextKey);
        if (occupied && occupied !== id) {
          throw new Error("Rack position is already occupied");
        }
        yRackOccupancy.set(nextKey, id);
      }

      const nextUpdates = {
        ...updates,
        dewarId: nextDewarId,
        dewarPosition: nextPosition,
        position: nextPosition,
        projectId:
          updates.projectId !== undefined
            ? updates.projectId
            : updates.project !== undefined
              ? updates.project
              : oldEntity.projectId,
        project:
          updates.project !== undefined
            ? updates.project
            : updates.projectId !== undefined
              ? updates.projectId
              : oldEntity.project,
        dateModified: new Date().toISOString(),
      };
      delete nextUpdates.removeRack;

      setEntityFields(rack, nextUpdates);
      const changes = computeFieldChanges(oldEntity, toPlain(rack));
      if (changes.length) appendPendingLog("rack", id, "updated", changes);
    }, "inventory:update-rack");
  }

  function deleteRack(id) {
    if (shouldUseScopedRest()) {
      return scopedDelete("racks", id);
    }
    ensureYjs(yRacks, "racks");
    ensureYjs(yPucks, "pucks");
    const rack = yRacks.get(id);
    if (!rack) throw new Error(`Rack not found: ${id}`);
    const plainRack = toPlain(rack);
    const now = new Date().toISOString();

    ydoc.transact(() => {
      if (plainRack.dewarId && plainRack.dewarPosition !== null && plainRack.dewarPosition !== undefined) {
        yRackOccupancy.delete(rackKey(plainRack.dewarId, plainRack.dewarPosition));
      }

      yPucks.forEach((puck, puckId) => {
        const plainPuck = toPlain(puck);
        if (plainPuck.deletedAt || plainPuck.rackId !== id) return;
        if (plainPuck.rackPosition !== null && plainPuck.rackPosition !== undefined) {
          yPuckOccupancy.delete(puckKey(id, plainPuck.rackPosition));
        }
        setEntityFields(puck, {
          rackId: null,
          rackPosition: null,
          position: null,
          dateModified: now,
        });
        appendPendingLog("puck", puckId, "updated", [
          { field: "rackId", from: plainPuck.rackId, to: null },
        ]);
      });

      setEntityFields(rack, { deletedAt: now, dateModified: now });
      appendPendingLog("rack", id, "deleted");
    }, "inventory:delete-rack");
  }

  function createPuck(data = {}) {
    if (shouldUseScopedRest()) {
      return scopedCreate("pucks", data);
    }
    ensureYjs(yPucks, "pucks");
    ensureYjs(yPuckOccupancy, "puckOccupancy");
    const id = data.id || crypto.randomUUID();
    const now = new Date().toISOString();
    const position = data.rackPosition ?? data.position ?? null;

    ydoc.transact(() => {
      if (data.rackId && position !== null && position !== undefined) {
        const key = puckKey(data.rackId, position);
        const occupied = yPuckOccupancy.get(key);
        if (occupied && occupied !== id) {
          throw new Error("Puck position is already occupied");
        }
      }

      const puck = new Y.Map();
      yPucks.set(id, puck);
      setEntityFields(puck, {
        id,
        id,
        title: data.title || data.name || "Puck",
        name: data.title || data.name || "Puck",
        description: data.description || "",
        customId: data.customId || "",
        puckType: data.puckType || "HPF",
        wellCount: Number(data.wellCount || 12),
        color: data.color || "chrome",
        rackId: data.rackId || null,
        rackPosition: position,
        position,
        projectId: data.projectId || data.project || null,
        project: data.project || data.projectId || null,
        serialNo: data.serialNo || data.customId || id,
        labId: data.labId || providerStore.currentLabId || "",
        creatorId: getMutationUserId(),
        dateCreated: data.dateCreated || now,
        dateModified: now,
      });

      if (data.rackId && position !== null && position !== undefined) {
        yPuckOccupancy.set(puckKey(data.rackId, position), id);
      }
      appendPendingLog("puck", id, "created");
    }, "inventory:create-puck");

    return id;
  }

  function updatePuck(id, updates = {}) {
    if (shouldUseScopedRest()) {
      return scopedUpdate("pucks", id, updates);
    }
    ensureYjs(yPucks, "pucks");
    const puck = yPucks.get(id);
    if (!puck) throw new Error(`Puck not found: ${id}`);
    const oldEntity = toPlain(puck);

    ydoc.transact(() => {
      const oldRackId = oldEntity.rackId || null;
      const oldPosition = oldEntity.rackPosition ?? oldEntity.position ?? null;
      const nextRackId = updates.rackId !== undefined ? updates.rackId : oldRackId;
      const nextPosition =
        updates.rackPosition !== undefined || updates.position !== undefined
          ? updates.rackPosition ?? updates.position ?? null
          : oldPosition;

      if (oldRackId && oldPosition !== null && oldPosition !== undefined) {
        yPuckOccupancy.delete(puckKey(oldRackId, oldPosition));
      }
      if (nextRackId && nextPosition !== null && nextPosition !== undefined) {
        const nextKey = puckKey(nextRackId, nextPosition);
        const occupied = yPuckOccupancy.get(nextKey);
        if (occupied && occupied !== id) {
          throw new Error("Puck position is already occupied");
        }
        yPuckOccupancy.set(nextKey, id);
      }

      setEntityFields(puck, {
        ...updates,
        rackId: nextRackId,
        rackPosition: nextPosition,
        position: nextPosition,
        projectId:
          updates.projectId !== undefined
            ? updates.projectId
            : updates.project !== undefined
              ? updates.project
              : oldEntity.projectId,
        project:
          updates.project !== undefined
            ? updates.project
            : updates.projectId !== undefined
              ? updates.projectId
              : oldEntity.project,
        dateModified: new Date().toISOString(),
      });

      const changes = computeFieldChanges(oldEntity, toPlain(puck));
      if (changes.length) appendPendingLog("puck", id, "updated", changes);
    }, "inventory:update-puck");
  }

  function deletePuck(id) {
    if (shouldUseScopedRest()) {
      return scopedDelete("pucks", id);
    }
    ensureYjs(yPucks, "pucks");
    ensureYjs(yBoxes, "boxes");
    const puck = yPucks.get(id);
    if (!puck) throw new Error(`Puck not found: ${id}`);
    const plainPuck = toPlain(puck);
    const now = new Date().toISOString();

    ydoc.transact(() => {
      if (plainPuck.rackId && plainPuck.rackPosition !== null && plainPuck.rackPosition !== undefined) {
        yPuckOccupancy.delete(puckKey(plainPuck.rackId, plainPuck.rackPosition));
      }

      yBoxes.forEach((box, boxId) => {
        const plainBox = toPlain(box);
        if (plainBox.deletedAt || plainBox.puckId !== id) return;
        yWellOccupancy.delete(wellKey(id, plainBox.wellNo));
        setEntityFields(box, { deletedAt: now, dateModified: now });
        appendPendingLog("box", boxId, "deleted");
        yGrids?.forEach((grid, gridId) => {
          const plainGrid = toPlain(grid);
          if (plainGrid.deletedAt || plainGrid.boxId !== boxId) return;
          setEntityFields(grid, { deletedAt: now, dateModified: now });
          appendPendingLog("grid", gridId, "deleted");
        });
      });

      setEntityFields(puck, { deletedAt: now, dateModified: now });
      appendPendingLog("puck", id, "deleted");
    }, "inventory:delete-puck");
  }

  function createBox(data = {}) {
    if (shouldUseScopedRest()) {
      return scopedCreate("boxes", data);
    }
    ensureYjs(yBoxes, "boxes");
    ensureYjs(yWellOccupancy, "wellOccupancy");
    const id = data.id || crypto.randomUUID();
    const now = new Date().toISOString();
    const key = wellKey(data.puckId, data.wellNo);
    const occupied = yWellOccupancy.get(key);
    if (occupied && occupied !== id) {
      throw new Error("This puck well is already occupied");
    }

    ydoc.transact(() => {
      const box = new Y.Map();
      yBoxes.set(id, box);
      const trueDateCreated = data.trueDateCreated || data.dateCreated || now;
      setEntityFields(box, {
        id,
        id,
        title: data.title || data.name || "",
        customId: data.customId || "",
        description: data.description || "",
        projectId: data.projectId || data.project || null,
        project: data.project || data.projectId || null,
        boxType: data.boxType || null,
        gridNumbering: data.gridNumbering || null,
        gridTopLeft: data.gridTopLeft || null,
        gridTopRight: data.gridTopRight || null,
        gridBottomLeft: data.gridBottomLeft || null,
        gridBottomRight: data.gridBottomRight || null,
        color: data.color || null,
        puckId: data.puckId,
        wellNo: Number(data.wellNo),
        labId: data.labId || providerStore.currentLabId || "",
        creatorId: getMutationUserId(),
        trueDateCreated,
        dateCreated: data.dateCreated || now,
        dateModified: now,
      });
      yWellOccupancy.set(key, id);
      applyLegacyGridSlotUpdates(id, data);
      appendPendingLog("box", id, "created");
    }, "inventory:create-box");

    return id;
  }

  function updateBox(id, updates = {}) {
    if (shouldUseScopedRest()) {
      return scopedUpdate("boxes", id, updates);
    }
    ensureYjs(yBoxes, "boxes");
    const box = yBoxes.get(id);
    if (!box) throw new Error(`Box not found: ${id}`);
    const oldEntity = toPlain(box);

    ydoc.transact(() => {
      const moveTo = updates.moveTo || null;
      const nextPuckId = moveTo?.toPuck || updates.puckId || oldEntity.puckId;
      const nextWellNo =
        moveTo?.toWell !== undefined
          ? Number(moveTo.toWell)
          : updates.wellNo !== undefined
            ? Number(updates.wellNo)
            : Number(oldEntity.wellNo);
      const oldKey = wellKey(oldEntity.puckId, oldEntity.wellNo);
      const nextKey = wellKey(nextPuckId, nextWellNo);

      if (oldKey !== nextKey) {
        const occupied = yWellOccupancy.get(nextKey);
        if (occupied && occupied !== id) {
          throw new Error("This puck well is already occupied");
        }
        yWellOccupancy.delete(oldKey);
        yWellOccupancy.set(nextKey, id);
      }

      const nextUpdates = {
        ...updates,
        puckId: nextPuckId,
        wellNo: nextWellNo,
        projectId:
          updates.projectId !== undefined
            ? updates.projectId
            : updates.project !== undefined
              ? updates.project
              : oldEntity.projectId,
        project:
          updates.project !== undefined
            ? updates.project
            : updates.projectId !== undefined
              ? updates.projectId
              : oldEntity.project,
        dateModified: new Date().toISOString(),
      };

      delete nextUpdates.moveTo;
      delete nextUpdates.samepuck;
      delete nextUpdates.originalPuckId;
      delete nextUpdates.originalWellNo;
      delete nextUpdates.gridupdate;
      delete nextUpdates.keepArchive;
      delete nextUpdates.imageurltodelete;
      delete nextUpdates.isGrid;
      delete nextUpdates.toGrid;
      delete nextUpdates.withGridData;

      applyLegacyGridSlotUpdates(id, nextUpdates);
      Object.keys(nextUpdates).forEach((key) => {
        if (gridSlotPosition(key)) {
          delete nextUpdates[key];
        }
      });
      setEntityFields(box, nextUpdates, { replaceSlots: true });
      const changes = computeFieldChanges(oldEntity, toPlain(box));
      if (changes.length) {
        appendPendingLog("box", id, oldKey !== nextKey ? "moved" : "updated", changes);
      }
    }, "inventory:update-box");
  }

  function deleteBox(id) {
    if (shouldUseScopedRest()) {
      return scopedDelete("boxes", id);
    }
    ensureYjs(yBoxes, "boxes");
    const box = yBoxes.get(id);
    if (!box) throw new Error(`Box not found: ${id}`);
    const plainBox = toPlain(box);

    ydoc.transact(() => {
      yWellOccupancy.delete(wellKey(plainBox.puckId, plainBox.wellNo));
      setEntityFields(box, {
        deletedAt: new Date().toISOString(),
        dateModified: new Date().toISOString(),
      });
      appendPendingLog("box", id, "deleted");
      yGrids?.forEach((grid, gridId) => {
        const plainGrid = toPlain(grid);
        if (plainGrid.deletedAt || plainGrid.boxId !== id) return;
        setEntityFields(grid, {
          deletedAt: new Date().toISOString(),
          dateModified: new Date().toISOString(),
        });
        appendPendingLog("grid", gridId, "deleted");
      });
    }, "inventory:delete-box");
  }

  function assignRackToDewar(rackId, dewarId) {
    return updateRack(rackId, {
      dewarId: dewarId || null,
      ...(dewarId ? {} : { dewarPosition: null, position: null }),
    });
  }

  function assignPuckToRack(puckId, rackId) {
    return updatePuck(puckId, {
      rackId: rackId || null,
      ...(rackId ? {} : { rackPosition: null, position: null }),
    });
  }

  function moveBoxToWell(boxId, puckId, wellNo) {
    return updateBox(boxId, { puckId, wellNo });
  }

  function createGrid(data = {}) {
    if (shouldUseScopedRest()) {
      return scopedCreate("grids", data);
    }
    ensureYjs(yGrids, "grids");
    const id = data.id || crypto.randomUUID();
    const now = new Date().toISOString();
    const placement = resolveGridPlacement(id, null, data);

    ydoc.transact(() => {
      const grid = new Y.Map();
      yGrids.set(id, grid);
      setEntityFields(grid, {
        id,
        id,
        boxId: placement.boxId,
        position: placement.position,
        name: data.name || "",
        description: data.description || "",
        gridType: data.gridType || [],
        gridCatalogType: data.gridCatalogType || null,
        forTomography: !!data.forTomography,
        imageurl: data.imageurl || null,
        imageRefs: data.imageRefs || [],
        archived: placement.archived,
        trueDateCreated: data.trueDateCreated || now,
        dateCreated: data.dateCreated || now,
        dateModified: now,
      });
      grid.delete("deletedAt");
      appendPendingLog("grid", id, "created");
    }, "inventory:create-grid");

    return id;
  }

  function updateGrid(id, updates = {}) {
    if (shouldUseScopedRest()) {
      return scopedUpdate("grids", id, updates);
    }
    ensureYjs(yGrids, "grids");
    ensureYjs(yLamellae, "lamellae");
    const grid = yGrids.get(id);
    if (!grid) throw new Error(`Grid not found: ${id}`);
    const oldEntity = toPlain(grid);
    const placement = resolveGridPlacement(id, oldEntity, updates);
    const now = new Date().toISOString();
    const isRestoringArchivedGrid = Boolean(oldEntity.archived) && !placement.archived;

    ydoc.transact(() => {
      setEntityFields(grid, {
        ...updates,
        boxId: placement.boxId,
        position: placement.position,
        archived: placement.archived,
        dateModified: now,
      });
      if (!placement.archived) {
        grid.delete("deletedAt");
      }
      if (isRestoringArchivedGrid) {
        restoreCascadeDeletedLamellaeForGrid(id, now);
      }
      const changes = computeFieldChanges(oldEntity, toPlain(grid));
      if (changes.length) appendPendingLog("grid", id, "updated", changes);
    }, "inventory:update-grid");
  }

  function deleteGrid(id) {
    if (shouldUseScopedRest()) {
      return scopedDelete("grids", id);
    }
    ensureYjs(yGrids, "grids");
    ensureYjs(yLamellae, "lamellae");
    const grid = yGrids.get(id);
    if (!grid) throw new Error(`Grid not found: ${id}`);
    const previousBoxId = canonicalId(grid.get("boxId"));
    const previousPosition = grid.get("position") ?? null;
    const now = new Date().toISOString();
    ydoc.transact(() => {
      setEntityFields(grid, {
        archived: true,
        previousBoxId,
        previousPosition,
        boxId: null,
        position: null,
        dateModified: now,
      });
      grid.delete("deletedAt");
      cascadeDeleteLamellaeForGrid(id, now);
      appendPendingLog("grid", id, "deleted");
    }, "inventory:delete-grid");
  }

  function createLamella(data = {}) {
    if (shouldUseScopedRest()) {
      return scopedCreate("lamellae", data);
    }
    ensureYjs(yLamellae, "lamellae");
    const grid = getGrid(data.gridId);
    if (!grid) {
      throw new Error(`Grid not found: ${data.gridId}`);
    }

    const id = data.id || crypto.randomUUID();
    const now = new Date().toISOString();

    ydoc.transact(() => {
      const lamella = new Y.Map();
      yLamellae.set(id, lamella);
      setEntityFields(lamella, {
        id,
        id,
        name: data.name || "New Lamella",
        gridId: data.gridId,
        labId: grid.labId || "",
        status: data.status || "pending",
        currentStage: data.currentStage || null,
        imageRefs: data.imageRefs || [],
        notes: data.notes || null,
        dateCreated: now,
        dateModified: now,
        deletedByParentCascade: false,
      });
      lamella.delete("deletedAt");
      appendPendingLog("lamella", id, "created");
    }, "inventory:create-lamella");

    return id;
  }

  function updateLamella(id, updates = {}) {
    if (shouldUseScopedRest()) {
      return scopedUpdate("lamellae", id, updates);
    }
    ensureYjs(yLamellae, "lamellae");
    const lamella = yLamellae.get(id);
    if (!lamella) throw new Error(`Lamella not found: ${id}`);
    const oldEntity = toPlain(lamella);

    ydoc.transact(() => {
      setEntityFields(lamella, {
        ...updates,
        imageRefs: updates.imageRefs ?? oldEntity.imageRefs ?? [],
        dateModified: new Date().toISOString(),
      });
      if (updates.deletedAt === undefined) {
        lamella.delete("deletedAt");
      }
      const changes = computeFieldChanges(oldEntity, toPlain(lamella));
      if (changes.length) appendPendingLog("lamella", id, "updated", changes);
    }, "inventory:update-lamella");
  }

  function deleteLamella(id) {
    if (shouldUseScopedRest()) {
      return scopedDelete("lamellae", id);
    }
    ensureYjs(yLamellae, "lamellae");
    const lamella = yLamellae.get(id);
    if (!lamella) throw new Error(`Lamella not found: ${id}`);

    ydoc.transact(() => {
      setEntityFields(lamella, {
        deletedAt: new Date().toISOString(),
        deletedByParentCascade: false,
        dateModified: new Date().toISOString(),
      });
      appendPendingLog("lamella", id, "deleted");
    }, "inventory:delete-lamella");
  }

  function restoreDewar(id) {
    if (shouldUseScopedRest()) {
      return scopedRestore("dewars", id);
    }
    ensureYjs(yDewars, "dewars");
    const dewar = yDewars.get(id);
    if (!dewar) throw new Error(`Dewar not found: ${id}`);
    ydoc.transact(() => {
      dewar.delete("deletedAt");
      setEntityFields(dewar, { dateModified: new Date().toISOString() });
      appendPendingLog("dewar", id, "restored");
    }, "inventory:restore-dewar");
  }

  function restoreRack(id) {
    if (shouldUseScopedRest()) {
      return scopedRestore("racks", id);
    }
    ensureYjs(yRacks, "racks");
    const rack = yRacks.get(id);
    if (!rack) throw new Error(`Rack not found: ${id}`);
    const plainRack = toPlain(rack);
    const next = { ...plainRack };
    const position = next.dewarPosition ?? next.position ?? null;
    ydoc.transact(() => {
      if (next.dewarId && position !== null && position !== undefined) {
        const key = rackKey(next.dewarId, position);
        const occupied = yRackOccupancy.get(key);
        if (occupied && occupied !== id) {
          next.dewarId = null;
          next.dewarPosition = null;
          next.position = null;
        } else {
          yRackOccupancy.set(key, id);
        }
      }
      delete next.deletedAt;
      setEntityFields(rack, { ...next, dateModified: new Date().toISOString() });
      rack.delete("deletedAt");
      appendPendingLog("rack", id, "restored");
    }, "inventory:restore-rack");
  }

  function restorePuck(id) {
    if (shouldUseScopedRest()) {
      return scopedRestore("pucks", id);
    }
    ensureYjs(yPucks, "pucks");
    const puck = yPucks.get(id);
    if (!puck) throw new Error(`Puck not found: ${id}`);
    const plainPuck = toPlain(puck);
    const next = { ...plainPuck };
    const position = next.rackPosition ?? next.position ?? null;
    ydoc.transact(() => {
      if (next.rackId && position !== null && position !== undefined) {
        const key = puckKey(next.rackId, position);
        const occupied = yPuckOccupancy.get(key);
        if (occupied && occupied !== id) {
          next.rackId = null;
          next.rackPosition = null;
          next.position = null;
        } else {
          yPuckOccupancy.set(key, id);
        }
      }
      delete next.deletedAt;
      setEntityFields(puck, { ...next, dateModified: new Date().toISOString() });
      puck.delete("deletedAt");
      appendPendingLog("puck", id, "restored");
    }, "inventory:restore-puck");
  }

  function restoreBox(id) {
    if (shouldUseScopedRest()) {
      return scopedRestore("boxes", id);
    }
    ensureYjs(yBoxes, "boxes");
    const box = yBoxes.get(id);
    if (!box) throw new Error(`Box not found: ${id}`);
    const plainBox = toPlain(box);
    const key = wellKey(plainBox.puckId, plainBox.wellNo);
    const occupied = yWellOccupancy.get(key);
    if (occupied && occupied !== id) {
      throw new Error("Cannot restore box into an occupied well");
    }
    ydoc.transact(() => {
      yWellOccupancy.set(key, id);
      box.delete("deletedAt");
      setEntityFields(box, { dateModified: new Date().toISOString() });
      appendPendingLog("box", id, "restored");
    }, "inventory:restore-box");
  }

  function restoreGrid(id) {
    if (shouldUseScopedRest()) {
      return scopedRestore("grids", id);
    }
    ensureYjs(yGrids, "grids");
    ensureYjs(yLamellae, "lamellae");
    const grid = yGrids.get(id);
    if (!grid) throw new Error(`Grid not found: ${id}`);
    const oldEntity = toPlain(grid);
    const placement = resolveGridPlacement(id, oldEntity, {
      archived: false,
      boxId: oldEntity.previousBoxId ?? oldEntity.boxId,
      position: oldEntity.previousPosition ?? oldEntity.position,
    });
    const now = new Date().toISOString();
    ydoc.transact(() => {
      setEntityFields(grid, {
        archived: false,
        boxId: placement.boxId,
        position: placement.position,
        dateModified: now,
      });
      grid.delete("deletedAt");
      restoreCascadeDeletedLamellaeForGrid(id, now);
      appendPendingLog("grid", id, "restored");
    }, "inventory:restore-grid");
  }

  function restoreLamella(id) {
    if (shouldUseScopedRest()) {
      return scopedRestore("lamellae", id);
    }
    ensureYjs(yLamellae, "lamellae");
    const lamella = yLamellae.get(id);
    if (!lamella) throw new Error(`Lamella not found: ${id}`);
    ydoc.transact(() => {
      lamella.delete("deletedAt");
      setEntityFields(lamella, {
        deletedByParentCascade: false,
        dateModified: new Date().toISOString(),
      });
      appendPendingLog("lamella", id, "restored");
    }, "inventory:restore-lamella");
  }

  function restoreDeletedEntity(type, id) {
    const normalized = `${type || ""}`.replace(/s$/, "");
    if (normalized === "dewar") return restoreDewar(id);
    if (normalized === "rack") return restoreRack(id);
    if (normalized === "puck") return restorePuck(id);
    if (normalized === "box") return restoreBox(id);
    if (normalized === "grid") return restoreGrid(id);
    if (normalized === "lamella") return restoreLamella(id);
    throw new Error(`Unsupported deleted inventory entity type: ${type}`);
  }

  const dewarTree = computed(() =>
    dewarsList.value.map((dewar) => ({
      ...clone(dewar),
      racks: getRacksForDewar(dewar.id).map((rack) => ({
        ...clone(rack),
        pucks: getPucksForRack(rack.id).map((puck) => {
          const payload = { ...clone(puck) };
          for (let well = 1; well <= 12; well += 1) {
            payload[`well${well}`] = null;
          }
          getBoxesForPuck(puck.id).forEach((box) => {
            const nextBox = clone(box);
            const boxGrids = getGridsForBox(box.id);
            boxGrids.forEach((grid) => {
              const slot = gridSlotName(Number(grid.position));
              if (slot) nextBox[slot] = clone(grid);
            });
            payload[`well${box.wellNo}`] = nextBox;
          });
          return payload;
        }),
      })),
    }))
  );

  function buildLegacyTree(activeLab) {
    if (!activeLab) return [];

    const activeLabRef = {
      ...activeLab,
      id: activeLab.id || activeLab.id,
    };

    return [
      {
        lab: activeLabRef.name,
        labId: activeLabRef.id,
        data: dewarTree.value.map((dewar) => ({
          ...clone(dewar),
          id: dewar.id || dewar.id,
          labId: activeLabRef,
          racks: (dewar.racks || []).map((rack) => ({
            ...clone(rack),
            id: rack.id || rack.id,
            labId: activeLabRef.id,
            pucks: (rack.pucks || []).map((puck) => ({
              ...clone(puck),
              id: puck.id || puck.id,
              labId: activeLabRef,
            })),
          })),
        })),
      },
    ];
  }

  async function connectActiveLab() {
    await providerStore.connect();
    if (providerStore.isSynced || providerStore.ydoc) {
      initializeFromYjs();
    }
  }

  async function disconnectActiveLab() {
    cleanup();
    await providerStore.disconnect();
  }

  watch(
    () => providerStore.ydoc,
    (nextDoc) => {
      if (!nextDoc) {
        cleanup();
      } else if (providerStore.isSynced) {
        initializeFromYjs();
      }
    },
    { immediate: true }
  );

  watch(
    () => providerStore.isSynced,
    (isSynced) => {
      if (isSynced && providerStore.ydoc) {
        initializeFromYjs();
      }
    },
    { immediate: true }
  );

  return {
    dewars,
    racks,
    pucks,
    boxes,
    grids,
    lamellae,
    wellOccupancy,
    rackOccupancy,
    puckOccupancy,
    mutationStatuses,
    dewarsList,
    racksList,
    pucksList,
    boxesList,
    gridsList,
    lamellaeList,
    deletedEntities,
    dewarTree,
    getUnassignedRacks,
    getUnassignedPucks,
    isInitialized,
    lastSyncTime,
    getDewar,
    getRack,
    getPuck,
    getBox,
    getGrid,
    getLamella,
    getRacksForDewar,
    getPucksForRack,
    getBoxesForPuck,
    getGridsForBox,
    getArchivedGrids,
    getLamellaeForGrid,
    getLamellaSummaryForGrid,
    getBoxAtWell,
    getMutationStatus,
    buildLegacyTree,
    connectActiveLab,
    disconnectActiveLab,
    syncFromYjs,
    createDewar,
    updateDewar,
    deleteDewar,
    createRack,
    updateRack,
    deleteRack,
    createPuck,
    updatePuck,
    deletePuck,
    createBox,
    updateBox,
    deleteBox,
    createGrid,
    createLamella,
    updateGrid,
    updateLamella,
    deleteGrid,
    deleteLamella,
    restoreDewar,
    restoreRack,
    restorePuck,
    restoreBox,
    restoreGrid,
    restoreLamella,
    restoreDeletedEntity,
    assignRackToDewar,
    assignPuckToRack,
    moveBoxToWell,
    moveGrid,
  };
});
